// The checkout return page (plan 10 §3). checkout.sara.ai redirects the shopper
// here with `?next=<chapter>&order_id=<id>&receipt=<jws>`. The query-param
// receipt is IGNORED as evidence — this page calls the confirmOrder action,
// which re-fetches the session server-side and verifies the receipt there. It
// polls through the webhook race (paid-but-receipt-not-issued-yet), then routes
// the reader back to the chapter, which — thanks to the reactive entitlement —
// renders unlocked with no reload.
//
// The poll is a genuine external side-effect on an interval, so it lives in an
// effect (react-useeffect-discipline's sanctioned case); an `active` guard makes
// StrictMode's double-mount and unmount safe, and confirmOrder is idempotent.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAction } from "convex/react";
import { ConvexError } from "convex/values";
import { Check, Loader2, LockKeyhole } from "lucide-react";
import { api } from "@bindery/convex/_generated/api";
import { signIn } from "@/lib/auth";
import { Button } from "@bindery/ui/components/ui/button";
import { useLogger } from "@/logging";

/** How long to poll the webhook race before telling the reader to check back. */
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_MS = 90_000;

type Phase =
  | "working"
  | "done"
  | "stalled"
  | "pending"
  | "expired"
  | "signedOut"
  | "failed";

/** Only follow a same-origin relative path — never an attacker-supplied absolute
 *  or protocol-relative URL smuggled through `?next=`. */
function safeNextPath(raw: string | null): string {
  if (raw === null) return "/";
  return raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";
}

/** A thrown Convex `unauthenticated` error (mirrors gloss-popover.tsx's
 *  `isUnauthenticated`): the broker session lapsed while checkout was open —
 *  a distinct, recoverable state, not a payment failure (review MINOR 5). */
function isUnauthenticated(error: unknown): boolean {
  return (
    error instanceof ConvexError &&
    typeof error.data === "object" &&
    error.data !== null &&
    (error.data as { code?: unknown }).code === "unauthenticated"
  );
}

/**
 * Fold a subscription confirm into the shape this page already handles. The
 * two lanes name the same states differently — a subscription becomes
 * `active`, a purchase is `granted` — and the page cares only about which of
 * its seven phases to render.
 */
function normalizeSubscription(result: {
  kind:
    | "active"
    | "already_active"
    | "pending"
    | "processing"
    | "expired"
    | "failed";
  reason?: string;
}): {
  kind:
    | "granted"
    | "already_granted"
    | "pending"
    | "processing"
    | "expired"
    | "failed";
} {
  switch (result.kind) {
    case "active":
      return { kind: "granted" };
    case "already_active":
      return { kind: "already_granted" };
    default:
      return { kind: result.kind };
  }
}

export function PurchaseComplete() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const nextPath = safeNextPath(params.get("next"));
  const navigate = useNavigate();
  // ONE return page for both lanes. checkout appends `order_id` to whatever
  // return_url it was handed, so the lane is carried in the url we built:
  // `?kind=subscription`. The two confirms are different actions (a signup
  // extends a window, a purchase grants an entitlement) but the poll, the
  // webhook race, and every phase the reader sees are identical — which is
  // exactly the code worth not writing twice.
  const isSubscription = params.get("kind") === "subscription";
  const confirmOrder = useAction(api.checkout.confirmOrder);
  const confirmSubscription = useAction(api.subscriptions.confirmSubscription);
  const log = useLogger();
  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  // A missing order id is knowable at render, so it's the initial phase — no
  // synchronous setState in the effect (react-useeffect-discipline).
  const [phase, setPhase] = useState<Phase>(
    orderId === null ? "failed" : "working",
  );

  useEffect(() => {
    if (orderId === null) return;
    let active = true;
    const startedAt = Date.now();

    const poll = async (): Promise<void> => {
      if (!active) return;
      try {
        const result = isSubscription
          ? normalizeSubscription(await confirmSubscription({ orderId }))
          : await confirmOrder({ orderId });
        if (!active) return;
        switch (result.kind) {
          case "granted":
          case "already_granted":
            logRef.current.event("paywall.complete", {
              order_id: orderId,
              result: result.kind,
            });
            setPhase("done");
            // Let the success state read for a beat, then return to the chapter.
            window.setTimeout(() => {
              if (active) void navigate(nextPath);
            }, 900);
            return;
          case "processing":
            setPhase("working");
            if (Date.now() - startedAt >= MAX_POLL_MS) {
              setPhase("stalled");
              return;
            }
            window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
            return;
          case "pending":
            setPhase("pending");
            return;
          case "expired":
            setPhase("expired");
            return;
          case "failed":
            setPhase("failed");
            return;
        }
      } catch (error) {
        if (active) setPhase(isUnauthenticated(error) ? "signedOut" : "failed");
      }
    };
    void poll();
    return () => {
      active = false;
    };
  }, [
    orderId,
    isSubscription,
    confirmOrder,
    confirmSubscription,
    navigate,
    nextPath,
  ]);

  return (
    <div className="bg-background flex min-h-svh flex-col items-center justify-center gap-6 px-6 text-center">
      <Indicator phase={phase} />
      <div className="max-w-md space-y-2">
        <h1 className="font-display text-foreground text-2xl font-black tracking-tight">
          {(isSubscription ? SUB_HEADINGS : HEADINGS)[phase]}
        </h1>
        <p className="text-muted-foreground font-serif">
          {(isSubscription ? SUB_MESSAGES : MESSAGES)[phase]}
        </p>
      </div>
      {phase !== "working" && phase !== "done" && (
        <div className="flex flex-col items-center gap-3">
          {phase === "signedOut" ? (
            <Button type="button" onClick={() => signIn()}>
              {isSubscription
                ? "Sign in to finish subscribing"
                : "Sign in to finish unlocking"}
            </Button>
          ) : (
            <Button asChild>
              <Link to={nextPath}>Back to the chapter</Link>
            </Button>
          )}
          <Link
            to="/"
            className="text-muted-foreground font-serif text-sm underline underline-offset-4"
          >
            Return to the library
          </Link>
        </div>
      )}
    </div>
  );
}

function Indicator({ phase }: { phase: Phase }) {
  const base =
    "flex size-16 items-center justify-center rounded-full border border-border bg-card";
  if (phase === "done") {
    return (
      <div className={`${base} text-primary`} aria-hidden="true">
        <Check className="size-7" />
      </div>
    );
  }
  if (phase === "working") {
    return (
      <div className={`${base} text-muted-foreground`} aria-hidden="true">
        <Loader2 className="size-7 animate-spin" />
      </div>
    );
  }
  return (
    <div className={`${base} text-muted-foreground`} aria-hidden="true">
      <LockKeyhole className="size-6" />
    </div>
  );
}

const HEADINGS: Record<Phase, string> = {
  working: "Confirming your purchase",
  done: "Unlocked — enjoy",
  stalled: "Still processing",
  pending: "Checkout not completed",
  expired: "This checkout expired",
  signedOut: "Sign in to finish unlocking",
  failed: "We couldn't confirm your purchase",
};

const MESSAGES: Record<Phase, string> = {
  working: "One moment while we verify your payment with checkout.",
  done: "Taking you back to where you left off…",
  stalled:
    "Your payment is taking longer than usual to settle. It will unlock automatically — check back on the chapter shortly.",
  pending: "It looks like the payment wasn't completed. You can try again.",
  expired: "The checkout session expired. Start a fresh purchase to continue.",
  signedOut:
    "Your session ended while we were confirming this. Sign in again to pick up right where you left off.",
  failed:
    "Something went wrong verifying your payment. No charge is confirmed — please try again.",
};

/**
 * The same seven phases, said for a membership. Separate maps rather than
 * conditionals inside the copy: the wording differs in almost every phase
 * ("unlocked" vs "your membership is live", "no charge is confirmed" vs
 * "you have not been subscribed"), and interleaving them would make both
 * harder to read and to change.
 */
const SUB_HEADINGS: Record<Phase, string> = {
  working: "Starting your membership",
  done: "You're in — welcome",
  stalled: "Still processing",
  pending: "Subscription not completed",
  expired: "This checkout expired",
  signedOut: "Sign in to finish subscribing",
  failed: "We couldn't start your membership",
};

const SUB_MESSAGES: Record<Phase, string> = {
  working: "One moment while we confirm the first payment with checkout.",
  done: "Taking you back to where you left off\u2026",
  stalled:
    "Your payment is taking longer than usual to settle. Your membership will start automatically \u2014 check back shortly.",
  pending: "It looks like the payment wasn't completed. You can try again.",
  expired: "The checkout session expired. Start again to subscribe.",
  signedOut:
    "Your session ended while we were confirming this. Sign in again and we'll pick it straight back up.",
  failed:
    "Something went wrong confirming the payment. You have not been subscribed \u2014 please try again.",
};
